// Shimmer that matches DefaultSeasonBar's card layout exactly (icon + 2-line
// text on the left, action buttons on the right) — used for version/revision
// detail pages, which render rows with DefaultSeasonBar rather than CustomSeasonBar.
export const DefaultSeasonBarShimmer = () => (
  <div className="mb-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className="h-10 w-10 animate-pulse rounded bg-gray-200" />
        <div className="flex flex-col gap-2">
          <div className="relative h-4 w-48 overflow-hidden rounded bg-gray-200">
            <div className="absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent" />
          </div>
          <div className="relative h-3 w-32 overflow-hidden rounded bg-gray-200">
            <div className="absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent" />
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="relative h-9 w-24 overflow-hidden rounded bg-gray-200">
          <div className="absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent" />
        </div>
        <div className="relative h-9 w-9 overflow-hidden rounded bg-gray-200">
          <div className="absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent" />
        </div>
      </div>
    </div>
  </div>
);

export const DefaultSeasonBarShimmerList = ({ count = 5 }: { count?: number }) => (
  <div className="space-y-3 p-6">
    {Array.from({ length: count }, (_, index) => (
      <DefaultSeasonBarShimmer key={index} />
    ))}
  </div>
);

// Skeleton that matches CustomSeasonBar's card layout exactly
// Icon: h-11 w-8 | Buttons: h-11 rounded-xl | Outer padding: p-4
const SeasonBarSkeletonCard = () => (
  <div className="mb-3 animate-pulse rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
    <div className="flex flex-wrap items-center justify-between gap-3 lg:flex-nowrap lg:gap-0">
      <div className="flex items-center gap-3">
        <div className="h-11 w-8 rounded bg-gray-200" />
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <div className="h-5 w-28 rounded bg-gray-200" />
            <div className="h-5 w-16 rounded bg-gray-200" />
          </div>
          <div className="h-3 w-32 rounded bg-gray-200 md:w-44" />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 lg:flex-nowrap lg:gap-3">
        <div className="h-9 w-16 rounded-xl bg-gray-200 lg:h-11 lg:w-20" />
        <div className="h-9 w-16 rounded-xl bg-gray-200 lg:h-11 lg:w-20" />
        <div className="h-9 w-16 rounded-xl bg-gray-200 lg:h-11 lg:w-20" />
        <div className="h-9 w-16 rounded-xl bg-gray-200 lg:h-11 lg:w-20" />
        <div className="h-5 w-2 rounded-xl bg-gray-200" />
      </div>
    </div>
  </div>
);

export const SeasonBarSkeleton = ({ count = 2 }: { count?: number }) => (
  <div className="flex flex-col gap-1 lg:gap-2">
    {Array.from({ length: count }).map((_, i) => (
      <SeasonBarSkeletonCard key={i} />
    ))}
  </div>
);

// Matches season_plan_index_page.tsx's (the Season Plan picker) actual
// layout exactly: heading, context line, then the Season/Plan dropdown row
// side by side - so a fresh/direct load to that route shows a shape close
// to the real page instead of a generic spinner or an unrelated skeleton.
const DropdownSkeleton = () => (
  <div className="w-full lg:w-1/2 2xl:max-w-md">
    <div className="mb-2 h-[14px] w-24 rounded bg-gray-200" />
    <div className="h-11 w-full rounded-xl bg-gray-200 lg:h-12" />
  </div>
);

export const SeasonPlanPickerSkeleton = () => (
  <div className="relative flex min-h-screen flex-col bg-gray-100 px-3 py-3 md:px-5 md:py-5">
    <div className="mb-4 animate-pulse rounded-lg bg-white p-4 shadow-sm md:mb-6 md:p-6">
      <div className="mb-1 h-5 w-32 rounded bg-gray-200" />
      <div className="mb-4 h-4 w-72 rounded bg-gray-200" />
      <div className="flex flex-col items-center gap-3 lg:flex-row lg:gap-6">
        <DropdownSkeleton />
        <DropdownSkeleton />
      </div>
    </div>
  </div>
);

interface LoadingSkeletonProps {
  count?: number;
}

export const LoadingSkeleton = ({ count = 4 }: LoadingSkeletonProps) => {
  return (
    <div className="space-y-6">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
};

const SkeletonCard = () => {
  return (
    <div className="animate-pulse rounded-lg bg-white p-4 shadow-sm md:p-6">
      {/* Header Section */}
      <div className="flex flex-col gap-4 border-b border-gray-200 pb-4 sm:flex-row sm:items-center sm:justify-between sm:pb-6">
        <div className="flex items-start gap-3 sm:gap-4">
          {/* Image Skeleton */}
          <div className="h-12 w-12 rounded-lg bg-gray-200 md:h-16 md:w-16" />

          {/* Title and Meta Info */}
          <div className="flex-1 space-y-3">
            {/* Season Title */}
            <div className="h-6 w-36 rounded bg-gray-200 md:w-48" />

            {/* Crop and Date Info */}
            <div className="flex flex-wrap items-center gap-3 md:gap-6">
              <div className="h-4 w-24 rounded bg-gray-200 md:w-32" />
              <div className="h-4 w-40 rounded bg-gray-200 md:w-64" />
            </div>
          </div>
        </div>

        {/* Button Skeleton */}
        <div className="h-9 w-full rounded-lg bg-gray-200 sm:h-10 sm:w-32" />
      </div>

      {/* Details Grid Section */}
      <div className="mb-6 py-4">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="space-y-2"
            >
              <div className="h-4 w-20 rounded bg-gray-200" />
              <div className="h-5 w-24 rounded bg-gray-200" />
            </div>
          ))}
        </div>
      </div>

      {/* Footer Section */}
      <div className="h-4 w-32 rounded bg-gray-200 md:w-40" />
    </div>
  );
};
